import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid, GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../../../shared/components/Header";
import CreateLearningRouteModal from "../components/CreateLearningRouteModal";
import DeleteConfirmationModal from "../components/DeleteLearningPathModal";
import {
  getLearningRoutes,
  deleteLearningRoute,
} from "../../../../../redux/actions/learningRoutes";
import { IRootState } from "../../../../../redux/types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export interface LearningRoutes {
  id: number;
  name: string;
  description?: string | null;
  courseIds: string[];
  level: "beginner" | "intermediate" | "high";
  order?: number;
  status?: boolean;
}

const AdminLearningRoutes = () => {
  const { t } = useTranslation(["global"]);
  const dispatch = useDispatch();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);

  const routes = useSelector(
    (state: IRootState) => state.learningRoutes.learningRoutes
  );

  useEffect(() => {
    dispatch(getLearningRoutes());
  }, [dispatch]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenModal = (id: number | null = null) => {
    setEditingId(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingId(null);
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: t("route-name"), flex: 1 },
    {
      field: "courses_count",
      headerName: t("courses"),
      flex: 1,
      renderCell: (params) => params.value || 0,
    },
    {
      field: "level",
      headerName: t("difficulty"),
      flex: 1,
      renderCell: (params) => t(`difficulty-levels.${params.value}`),
    },
    {
      field: "actions",
      headerName: t("actions"),
      type: "actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label={t("edit")}
          onClick={() => handleOpenModal(params.row.id)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label={t("delete")}
          onClick={() => {
            setSelectedRouteId(params.row.id);
            setDeleteModalOpen(true);
          }}
          showInMenu
        />,
      ],
    }
  ];

  return (
    <>
      <Header title={t("learning-routes")} />
      <Container maxWidth="lg">
        <Box
          mt={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal()}
          >
            {t("create-learning-route")}
          </Button>
        </Box>

        <Box mt={4} component={Paper} height={500}>
          <DataGrid
            rows={routes.filter((r) => typeof r === "object" && r !== null && r.id)}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
          />
        </Box>

        <CreateLearningRouteModal
          open={openModal}
          onClose={handleCloseModal}
          editingId={editingId}
          onSuccess={(msg) => {
            setSnackbar({ open: true, message: msg, severity: "success" });
          }}
          onError={(msg) => {
            setSnackbar({ open: true, message: msg, severity: "error" });
          }}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedRouteId(null);
          }}
          onConfirm={async () => {
            if (selectedRouteId !== null) {
              try {
                await dispatch(deleteLearningRoute(selectedRouteId));
                dispatch(getLearningRoutes());
                setSnackbar({
                  open: true,
                  message: t("deleteSuccess"),
                  severity: "success",
                });
                } catch (error: unknown) {
                  setSnackbar({
                    open: true,
                    message: (error as any)?.message || t("deleteError"),
                    severity: "error",
                  });
                } finally {
                setDeleteModalOpen(false);
                setSelectedRouteId(null);
              }
            }
          }}
        />
      </Container>
    </>
  );
};

export default AdminLearningRoutes;
